import {
  CAMPAIGN_CONFIG_SUBJECT,
  CampaignConfigSchema,
  isConfigSet,
  type CampaignConfig,
} from './config-ops.js';
import { foldOpsIntoEntities, type EntityState } from './entities-fold.js';

/**
 * The folded campaign-settings record (#171, ADR-0004 §5). A `Partial`
 * because the op log only carries the keys a GM has actually written; unset
 * keys are absent (callers default at read time). Each value is typed by its
 * key via `CampaignConfig` — `config.prose_voice` is the prose-voice enum,
 * `config.accent_color` is the hex-color string, etc.
 *
 * This is the "config" current-value bucket of the three-bucket model
 * (fact / setting / definition). It is a rebuildable view of the op log —
 * never authoritative — exactly like the entities map. The same fold runs in
 * the client replica and in DOSQL so the two stay at schema parity.
 */
export type ConfigState = Partial<CampaignConfig>;

/**
 * The minimal op shape the config fold reads. A structural subset of the op
 * envelope shared with `foldOpsIntoEntities`'s `OpLike` — config ops carry
 * no reveal / validity / belief, so only the subject and payload matter for
 * routing.
 */
interface ConfigOpLike {
  subject: string;
  payload: unknown;
}

/**
 * Fold an HLC-ordered op stream into the campaign `config` record.
 *
 * Routing: an op participates iff its `subject === CAMPAIGN_CONFIG_SUBJECT`
 * AND its payload is a `config.set`. Everything else (entity / fact / reveal /
 * pack ops) is skipped — config lives in its own bucket, never the entities
 * map. A `config.set` riding any other subject is ignored as a defence-in-depth
 * measure: a config op MUST travel under the reserved subject.
 *
 * Merge = **last-writer-wins per key, silently**. Each config key is its own
 * independent chain; the stream is HLC-ordered, so the last write to a key in
 * the stream wins. There is deliberately **no** draft-with-conflict path — the
 * "no silent overwrite" invariant governs canon facts, not GM operational
 * preferences (ADR-0004 §5).
 *
 * Each value is validated against its key's schema; an invalid value is
 * dropped silently (the DO is the write-time gate per #176, but the fold is
 * defensive so a malformed op can never corrupt the rebuilt record). A
 * dropped invalid write does NOT clear a previously-valid value for that key.
 *
 * Unknown keys are dropped (no matching union arm in `ConfigSetPayloadSchema`),
 * which keeps the fold forward-compatible if a newer client writes a key this
 * one doesn't know.
 */
export function foldConfigOps(ops: readonly ConfigOpLike[]): ConfigState {
  const config: ConfigState = {};

  for (const op of ops) {
    if (op.subject !== CAMPAIGN_CONFIG_SUBJECT) continue;
    if (!isConfigSet(op.payload)) continue;

    const { key, value } = op.payload;
    // Validate the value against this key's schema. `CampaignConfigSchema` is
    // the single source of truth for the per-key value type; an unknown key
    // has no shape entry and is dropped, a mistyped value fails its schema and
    // is dropped — neither disturbs the existing record.
    // Look up ONLY own keys of the schema shape. A crafted `key` of `__proto__` or
    // `constructor` would otherwise resolve to `Object.prototype`/`Object` (truthy,
    // but with no `.safeParse`), throwing a TypeError below and aborting the entire
    // config fold — the exact "a malformed op can never corrupt the record" contract
    // this fold documents. Own-key check drops them silently like any unknown key.
    if (!Object.hasOwn(CampaignConfigSchema.shape, key)) continue;
    const valueSchema = CampaignConfigSchema.shape[key];
    if (!valueSchema) continue;
    const parsed = valueSchema.safeParse(value);
    if (!parsed.success) continue;

    // Last-writer-wins: a later op in HLC order overwrites the key's slot.
    // `key`/`value` are correlated through the `config.set` union arm, but TS
    // widens `config[key]` to `never` across the union, so we assign through a
    // single typed sink.
    (config as Record<string, unknown>)[key] = parsed.data;
  }

  return config;
}

/**
 * The combined campaign projection: the entities map AND the config record,
 * folded from one op stream in a single pass-pair. This is the convenience
 * chokepoint a campaign consumer (client `app.ts`, the DO) binds against —
 * it guarantees config ops and entity/fact ops are routed to disjoint buckets
 * from the same source log.
 *
 * Both sub-folds are stream-order-sensitive and assume HLC-ordered input (the
 * same contract as `foldOpsIntoEntities`). They share the input but never the
 * output: config ops are inert in the entities fold (no entity at the reserved
 * subject; `config.set` is neither a fact nor a reveal payload), and entity /
 * fact ops are skipped by the config fold.
 */
export interface CampaignState {
  entities: Record<string, EntityState>;
  config: ConfigState;
}

export function foldOpsIntoCampaign(
  // The entities `OpLike` is a superset of `ConfigOpLike` (it also carries
  // `subject` + `payload`), so one parameter type feeds both sub-folds.
  ops: Parameters<typeof foldOpsIntoEntities>[0],
): CampaignState {
  return {
    entities: foldOpsIntoEntities(ops),
    config: foldConfigOps(ops),
  };
}
